import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { PostCard } from '@/components/PostCard';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Plus, Loader2, Plane, Users, Globe } from 'lucide-react';

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [followingPosts, setFollowingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFollowing, setLoadingFollowing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreFollowing, setHasMoreFollowing] = useState(true);
  const [page, setPage] = useState(0);
  const [pageFollowing, setPageFollowing] = useState(0);
  const [activeTab, setActiveTab] = useState('all');

  const fetchPosts = async (pageNum = 0) => {
    try {
      const response = await axios.get(`${API}/posts`, {
        params: { skip: pageNum * 20, limit: 20 },
        withCredentials: true
      });
      
      if (pageNum === 0) {
        setPosts(response.data);
      } else {
        setPosts(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.data.length === 20);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowingPosts = async (pageNum = 0) => {
    setLoadingFollowing(true);
    try {
      const response = await axios.get(`${API}/feed/following`, {
        params: { skip: pageNum * 20, limit: 20 },
        withCredentials: true
      });
      
      if (pageNum === 0) {
        setFollowingPosts(response.data);
      } else {
        setFollowingPosts(prev => [...prev, ...response.data]);
      }
      
      setHasMoreFollowing(response.data.length === 20);
    } catch (error) {
      console.error('Failed to fetch following posts:', error);
    } finally {
      setLoadingFollowing(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (activeTab === 'following' && followingPosts.length === 0) {
      fetchFollowingPosts();
    }
  }, [activeTab]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage);
  };

  const loadMoreFollowing = () => {
    const nextPage = pageFollowing + 1;
    setPageFollowing(nextPage);
    fetchFollowingPosts(nextPage);
  };

  const handleLikeUpdate = (postId, liked, count) => {
    setPosts(posts.map(p => 
      p.post_id === postId 
        ? { ...p, is_liked: liked, likes_count: count }
        : p
    ));
    setFollowingPosts(followingPosts.map(p => 
      p.post_id === postId 
        ? { ...p, is_liked: liked, likes_count: count }
        : p
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="feed-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="feed-page">
      {/* Header */}
      <div className="mb-8">
        <h1 
          className="text-3xl sm:text-4xl font-bold text-white mb-2"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-gray-400">Discover the latest window views from around the world</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
        <TabsList className="bg-white/5 border border-white/10 p-1">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-white px-6"
          >
            <Globe className="w-4 h-4 mr-2" />
            All Views
          </TabsTrigger>
          <TabsTrigger 
            value="following" 
            className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-white px-6"
          >
            <Users className="w-4 h-4 mr-2" />
            Following
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {posts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto mb-6">
                <Plane className="w-10 h-10 text-purple-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">No posts yet</h2>
              <p className="text-gray-400 mb-6">Be the first to share a window view!</p>
              <Link to="/upload">
                <Button className="btn-primary-gradient rounded-full px-6 py-3">
                  <Plus className="w-4 h-4 mr-2" />
                  Upload Your First View
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="bento-grid">
                {posts.map((post, index) => (
                  <PostCard 
                    key={post.post_id} 
                    post={post} 
                    onLikeUpdate={handleLikeUpdate}
                    variant={index % 5 === 0 ? 'tall' : 'default'}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-12">
                  <Button 
                    onClick={loadMore}
                    className="btn-secondary rounded-full px-8 py-3 text-white"
                  >
                    Load More
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="following" className="mt-6">
          {loadingFollowing && followingPosts.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : followingPosts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-purple-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">No posts from people you follow</h2>
              <p className="text-gray-400 mb-6">Start following other travelers to see their window views here!</p>
              <Link to="/explore">
                <Button className="btn-primary-gradient rounded-full px-6 py-3">
                  Discover People to Follow
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="bento-grid">
                {followingPosts.map((post, index) => (
                  <PostCard 
                    key={post.post_id} 
                    post={post} 
                    onLikeUpdate={handleLikeUpdate}
                    variant={index % 5 === 0 ? 'tall' : 'default'}
                  />
                ))}
              </div>

              {hasMoreFollowing && (
                <div className="text-center mt-12">
                  <Button 
                    onClick={loadMoreFollowing}
                    disabled={loadingFollowing}
                    className="btn-secondary rounded-full px-8 py-3 text-white"
                  >
                    {loadingFollowing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Floating Upload Button */}
      <Link to="/upload" className="fab" data-testid="fab-upload">
        <Plus className="w-6 h-6 text-white" />
      </Link>
    </div>
  );
}

export { Feed };
